import type { ReactNode } from 'react';
import { cx } from '../cx';
import { LABEL_TEXT, TICK_TEXT, niceScale, percentOf, thinTick } from './scale';
import { WIDE_ONLY, type ChartLayout } from './layout';
import { ROUGH_DOT, sampleTier, type ThinLabels } from './thin';

export type DotItem = {
  id: string;
  label: string;
  value: number;
  /** How many values the dot rests on: 10 to 19 draws a hollow dashed dot, under 10 leaves it out. */
  sample?: number;
};
export type DotGroup = { id: string; label: ReactNode; items: DotItem[] };

export type DotStripProps = {
  /** One row per group; one dot per item (each item computed from at least 10 values). */
  groups: DotGroup[];
  /** Items named on the chart: sun with an ink ring and their name under the dot. */
  highlightIds?: string[];
  /** A reference line across every row (the median of everything), dashed, with its label. */
  reference?: { value: number; label: ReactNode };
  formatTick: (value: number) => string;
  /** The thin-data words, translated; a named rough dot gets "rough" after its name. */
  thinLabels?: ThinLabels;
  /** `auto` (default) switches to the narrow form in a narrow container: each group label above its row, the plot full width. */
  layout?: ChartLayout;
  /** One sentence that states the finding, for screen readers. */
  description: string;
  className?: string;
};

/** Nudge dots that would overlap within a row up and down (offsets in rem). */
export function dotOffsets(positions: number[], minGap = 1.6): number[] {
  const order = positions.map((p, i) => ({ p, i })).sort((a, b) => a.p - b.p);
  const out = positions.map(() => 0);
  let run = 0;
  for (let k = 1; k < order.length; k++) {
    run = order[k].p - order[k - 1].p < minGap ? run + 1 : 0;
    out[order[k].i] = run === 0 ? 0 : (run % 2 === 1 ? -1 : 1) * 0.35 * Math.ceil(run / 2);
  }
  return out;
}

/**
 * Many items on one shared scale, grouped in rows: every role's median pay, grouped by field.
 * Highlighted items are named; the rest are context. Overlapping dots are nudged, never hidden.
 * Items with a `sample` follow the thin-data look: hollow and dashed when rough, gone when too few.
 */
const ROW: Record<ChartLayout, string> = {
  auto: 'flex flex-col @md:flex-row @md:items-center @md:gap-3',
  narrow: 'flex flex-col',
  wide: 'flex items-center gap-3',
};
const ROW_LABEL: Record<ChartLayout, string> = {
  auto: '@md:w-1/4 @md:shrink-0',
  narrow: '',
  wide: 'w-1/4 shrink-0',
};
// In a column, flex-1 would collapse the plot to no height: the narrow form sizes it by width.
const PLOT: Record<ChartLayout, string> = { auto: '@md:flex-1', narrow: '', wide: 'flex-1' };
// The reference line spans the plots, so it starts where they do: after the label column, or at the left edge.
const REFERENCE: Record<ChartLayout, string> = {
  auto: 'left-0 @md:left-1/4 @md:ml-3',
  narrow: 'left-0',
  wide: 'left-1/4 ml-3',
};

export function DotStrip({
  groups: allGroups,
  highlightIds = [],
  reference,
  formatTick,
  thinLabels,
  layout = 'auto',
  description,
  className,
}: DotStripProps) {
  const groups = allGroups.map((g) => ({
    ...g,
    items: g.items.filter((i) => i.sample === undefined || sampleTier(i.sample) !== 'none'),
  }));
  const values = groups.flatMap((g) => g.items.map((i) => i.value));
  const scale = niceScale(
    Math.min(...values, reference?.value ?? Infinity),
    Math.max(...values, reference?.value ?? -Infinity),
  );
  const pos = (v: number) => percentOf(v, scale.min, scale.max);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      <div className="relative flex flex-col">
        {reference ? (
          <div className={cx('pointer-events-none absolute inset-y-0 right-4', REFERENCE[layout])} aria-hidden="true">
            <span
              className="absolute inset-y-0 border-l-2 border-dashed border-jc-ink-secondary"
              style={{ left: `${pos(reference.value)}%` }}
            >
              <span className="absolute top-0 left-1.5 text-xs font-semibold whitespace-nowrap text-jc-ink-secondary">
                {reference.label}
              </span>
            </span>
          </div>
        ) : null}
        <div className={cx('flex flex-col', reference && 'pt-6')}>
          {groups.map((g) => {
            const offsets = dotOffsets(g.items.map((i) => pos(i.value)));
            return (
              <div key={g.id} className={ROW[layout]}>
                <span className={cx('truncate', LABEL_TEXT, ROW_LABEL[layout])}>
                  {/* On the surface colour, so the reference line passes behind the name in the narrow form. */}
                  <span className="relative z-10 bg-jc-surface pr-1">{g.label}</span>
                </span>
                <div className={cx('relative mr-4 h-12', PLOT[layout])}>
                  <span className="absolute inset-x-0 top-4 border-t border-jc-chart-grid" />
                  {g.items.map((item, k) => {
                    const named = highlightIds.includes(item.id);
                    const rough = item.sample !== undefined && sampleTier(item.sample) === 'rough';
                    return (
                      <span
                        key={item.id}
                        className="absolute"
                        style={{ left: `${pos(item.value)}%`, top: `calc(1rem + ${offsets[k]}rem)` }}
                      >
                        <span
                          className={cx(
                            'absolute -translate-x-1/2 -translate-y-1/2 rounded-full',
                            rough
                              ? cx(ROUGH_DOT, named ? 'z-10 size-3' : 'size-2.5')
                              : named
                                ? 'z-10 size-3 bg-jc-chart-2 ring-2 ring-jc-ink'
                                : 'size-2.5 bg-jc-chart-context',
                          )}
                        />
                        {named ? (
                          <span className="absolute top-2 -translate-x-1/2 text-xs font-semibold whitespace-nowrap text-jc-ink">
                            {item.label}
                            {rough && thinLabels ? <>, {thinLabels.rough}</> : null}
                          </span>
                        ) : null}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex gap-3" aria-hidden="true">
        <span className={cx('w-1/4 shrink-0', WIDE_ONLY[layout])} />
        <div className="relative mr-4 h-4 flex-1">
          {scale.ticks.map((t, i) => (
            <span
              key={t}
              className={cx('absolute -translate-x-1/2 whitespace-nowrap', TICK_TEXT, thinTick(i, scale.ticks.length))}
              style={{ left: `${pos(t)}%` }}
            >
              {formatTick(t)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
